import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getPackages, getProjectStats, getProjectPhases, transitionPackage, getWorkQueue } from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';

const LIFECYCLE_COLUMNS = [
  { key: 'scope',  label: 'Scope',       states: ['draft_scope','scope_review','ready_for_rfq'] },
  { key: 'rfq',    label: 'RFQ',         states: ['rfq_drafted','rfq_approved','rfq_sent'] },
  { key: 'quotes', label: 'Quotes',      states: ['awaiting_quotes','quotes_received'] },
  { key: 'award',  label: 'Award',       states: ['bid_leveling','award_recommendation','award_approved'] },
  { key: 'post',   label: 'Contract/PO', states: ['contract_po_pending','contract_po_issued'] },
  { key: 'site',   label: 'Site',        states: ['submittals_pending','submittals_approved','delivery_scheduled','delivered'] },
  { key: 'invoice',label: 'Invoice',     states: ['invoice_pending','invoice_matched'] },
  { key: 'closed', label: 'Closed',      states: ['closed'] },
];

const PHASES = ['Groundworks','Foundation','Structure','Facade','Interior','Handover'];

const RISK_COLORS = {
  low:    { color: 'var(--color-success)', bg: '#ECFDF5' },
  medium: { color: 'var(--color-warning)', bg: '#FFFBEB' },
  high:   { color: 'var(--color-error)',   bg: '#FEF2F2' },
};

function getColumnForState(state) {
  for (const col of LIFECYCLE_COLUMNS) {
    if (col.states.includes(state)) return col.key;
  }
  return 'scope';
}

export default function ProjectControl() {
  const { selectedProject, addToast } = useOutletContext();
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState(null);
  const [phases, setPhases] = useState([]);
  const [workQueue, setWorkQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadAll();
  }, [selectedProject]);

  async function loadAll() {
    setLoading(true);
    try {
      const [pkgs, statsData, phasesData, wq] = await Promise.all([
        getPackages(selectedProject.id),
        getProjectStats(selectedProject.id),
        getProjectPhases(selectedProject.id),
        getWorkQueue(selectedProject.id),
      ]);
      setPackages(pkgs || []);
      setStats(statsData || {});
      setPhases(phasesData || []);
      setWorkQueue(wq || {});
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  // KPI calculations
  const activePackages = packages.length;
  const rfqDueThisWeek = packages.filter(p => {
    if (!p.rfq_due_date) return false;
    const due = new Date(p.rfq_due_date);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  }).length;
  const awaitingReview = packages.filter(p =>
    ['quotes_received','bid_leveling','award_recommendation'].includes(p.lifecycle_state)
  ).length;
  const awardPending = packages.filter(p =>
    p.lifecycle_state === 'award_recommendation'
  ).length;
  const delayed = packages.filter(p => {
    if (!p.rfq_due_date) return false;
    return new Date(p.rfq_due_date) < new Date() &&
      !['award_approved','closed'].includes(p.lifecycle_state);
  }).length;

  // Action intelligence
  const actions = [];
  const rfqReady = packages.filter(p => p.lifecycle_state === 'ready_for_rfq');
  const quotesReady = packages.filter(p => p.lifecycle_state === 'quotes_received');
  const awardReady = packages.filter(p => p.lifecycle_state === 'award_recommendation');
  const delayedPkgs = packages.filter(p => {
    if (!p.rfq_due_date) return false;
    return new Date(p.rfq_due_date) < new Date() && !['award_approved','closed'].includes(p.lifecycle_state);
  });

  if (rfqReady.length) actions.push({ type: 'rfq', label: `Send RFQs for ${rfqReady.length} package${rfqReady.length > 1 ? 's' : ''}`, pkgs: rfqReady, color: 'var(--color-info)', icon: '✉' });
  if (quotesReady.length) actions.push({ type: 'review', label: `Review quotes for ${quotesReady.length} package${quotesReady.length > 1 ? 's' : ''}`, pkgs: quotesReady, color: 'var(--color-warning)', icon: '⚖' });
  if (awardReady.length) actions.push({ type: 'award', label: `Approve ${awardReady.length} award recommendation${awardReady.length > 1 ? 's' : ''}`, pkgs: awardReady, color: 'var(--color-success)', icon: '★' });
  if (delayedPkgs.length) actions.push({ type: 'delayed', label: `${delayedPkgs.length} package${delayedPkgs.length > 1 ? 's' : ''} overdue`, pkgs: delayedPkgs, color: 'var(--color-error)', icon: '⚑' });
  if (workQueue?.low_confidence_items > 0) actions.push({ type: 'extraction', label: `${workQueue.low_confidence_items} items need review`, pkgs: [], color: 'var(--color-warning)', icon: '◈' });

  if (loading && selectedProject?.id) return (
    <div className="page">
      <div className="empty-state"><div className="spinner" /></div>
    </div>
  );

  if (!selectedProject?.id) return (
    <div className="page">
      <div className="empty-state" style={{ minHeight: 400 }}>
        <div className="empty-state-icon">⬡</div>
        <h3>No project selected</h3>
        <p>Create your first project to get started with procurement</p>
        <button className="btn btn-primary" onClick={() => {
          const name = prompt('Project name:');
          if (!name) return;
          const location = prompt('Location (e.g. London EC2):') || '';
          const client = prompt('Client name:') || '';
          import('../utils/api').then(({ createProject }) => {
            createProject({ name, location, client, project_type: 'Commercial' }).then(() => {
              window.location.reload();
            });
          });
        }}>+ Create Project</button>
      </div>
    </div>
  );

  return (
    <div className="page">

      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Project Control</div>
          <div className="page-subtitle">
            {selectedProject?.name || 'No project selected'} · AI Procurement Department
          </div>
        </div>
        <div className="page-actions">
          <button onClick={() => navigate('/work-queue')} className="btn btn-secondary btn-sm">
            ✦ AI Work Queue {workQueue?.packages_needing_action?.length > 0 && `(${workQueue.packages_needing_action.length})`}
          </button>
          <button onClick={() => navigate('/packages')} className="btn btn-primary">
            + New Package
          </button>
        </div>
      </div>

      {/* KPI Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Active Packages',    value: activePackages,   border: 'var(--color-teal)',    alert: false },
          { label: 'RFQs Due This Week', value: rfqDueThisWeek,   border: 'var(--color-info)',    alert: rfqDueThisWeek > 0 },
          { label: 'Quotes to Review',   value: awaitingReview,   border: 'var(--color-warning)', alert: awaitingReview > 0 },
          { label: 'Award Decisions',    value: awardPending,     border: 'var(--color-success)', alert: awardPending > 0 },
          { label: 'Delayed Packages',   value: delayed,          border: 'var(--color-error)',   alert: delayed > 0 },
        ].map(kpi => (
          <div key={kpi.label} className="kpi-card" style={{ borderLeft: `3px solid ${kpi.border}` }}>
            <div className="kpi-card-label">{kpi.label}</div>
            <div className="kpi-card-value" style={{ color: kpi.alert && kpi.value > 0 ? kpi.border : 'var(--color-text-dark)' }}>
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Main layout: lifecycle board + action panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>

        {/* Lifecycle Board */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Phase Timeline */}
          {phases.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Construction Phases</span>
              </div>
              <div style={{ padding: '12px 20px', display: 'flex', gap: 0, overflowX: 'auto' }}>
                {(phases.length > 0 ? phases : PHASES.map((name, i) => ({ name, phase_order: i + 1 }))).map((phase, i, arr) => (
                  <div key={phase.id || i} style={{ flex: 1, minWidth: 80, position: 'relative' }}>
                    <div style={{
                      padding: '8px 12px',
                      background: phase.delay_days > 0 ? '#FEF2F2' : 'var(--color-teal-light)',
                      border: `1px solid ${phase.delay_days > 0 ? 'var(--color-error)' : 'var(--color-teal)'}`,
                      borderRight: i < arr.length - 1 ? 'none' : undefined,
                      borderRadius: i === 0 ? 'var(--radius-md) 0 0 var(--radius-md)' : i === arr.length - 1 ? '0 var(--radius-md) var(--radius-md) 0' : 0,
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: phase.delay_days > 0 ? 'var(--color-error)' : 'var(--color-teal)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {phase.name}
                      </div>
                      {phase.delay_days > 0 && (
                        <div style={{ fontSize: '0.6875rem', color: 'var(--color-error)' }}>+{phase.delay_days}d delay</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Package Lifecycle Board */}
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Package Lifecycle Board</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>{packages.length} packages</span>
            </div>

            {/* Column headers */}
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 900 }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `200px repeat(${LIFECYCLE_COLUMNS.length}, 1fr)`,
                  borderBottom: '1px solid var(--color-border)',
                  background: 'var(--color-background)',
                }}>
                  <div style={{ padding: '10px 16px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Package
                  </div>
                  {LIFECYCLE_COLUMNS.map(col => (
                    <div key={col.key} style={{ padding: '10px 8px', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', borderLeft: '1px solid var(--color-border)' }}>
                      {col.label}
                    </div>
                  ))}
                </div>

                {/* Package rows */}
                {packages.length === 0 ? (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-state-icon">▦</div>
                    <h3>No packages yet</h3>
                    <p>Create your first procurement package to get started</p>
                    <button onClick={() => navigate('/packages')} className="btn btn-primary btn-sm">+ Create Package</button>
                  </div>
                ) : (
                  packages.map(pkg => {
                    const state = pkg.lifecycle_state || 'draft_scope';
                    const activeCol = getColumnForState(state);
                    const risk = RISK_COLORS[pkg.risk_level] || RISK_COLORS.low;
                    const isSelected = selectedPkg?.id === pkg.id;

                    return (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPkg(isSelected ? null : pkg)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `200px repeat(${LIFECYCLE_COLUMNS.length}, 1fr)`,
                          borderBottom: '1px solid var(--color-border)',
                          background: isSelected ? 'var(--color-teal-light)' : 'transparent',
                          cursor: 'pointer',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--color-background)'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {/* Package name column */}
                        <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 3, borderRight: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pkg.name}
                          </div>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <span style={{ fontSize: '0.6875rem', fontFamily: 'var(--font-mono)', color: 'var(--color-text-light)' }}>
                              PKG-{String(pkg.id).padStart(3, '0')}
                            </span>
                            {pkg.risk_level && pkg.risk_level !== 'low' && (
                              <span style={{ fontSize: '0.6875rem', padding: '1px 5px', borderRadius: 'var(--radius-full)', background: risk.bg, color: risk.color, fontWeight: 600 }}>
                                {pkg.risk_level}
                              </span>
                            )}
                          </div>
                          {pkg.trade_category && (
                            <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-light)' }}>{pkg.trade_category}</div>
                          )}
                        </div>

                        {/* Lifecycle columns */}
                        {LIFECYCLE_COLUMNS.map(col => {
                          const isActive = col.key === activeCol;
                          return (
                            <div key={col.key} style={{
                              padding: '10px 8px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              borderLeft: '1px solid var(--color-border)',
                              background: isActive ? 'rgba(13,148,136,0.08)' : 'transparent',
                            }}>
                              {isActive && (
                                <div style={{
                                  width: 10, height: 10, borderRadius: '50%',
                                  background: 'var(--color-teal)',
                                  boxShadow: '0 0 0 3px rgba(13,148,136,0.2)',
                                }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Selected package detail */}
          {selectedPkg && (
            <div className="card" style={{ animation: 'fadeIn 200ms ease' }}>
              <div className="card-header">
                <div>
                  <div style={{ fontWeight: 600 }}>{selectedPkg.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginTop: 2 }}>
                    PKG-{String(selectedPkg.id).padStart(3, '0')} · {selectedPkg.trade_category}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <LifecyclePill state={selectedPkg.lifecycle_state} />
                  <button onClick={() => navigate(`/packages/${selectedPkg.id}`)} className="btn btn-primary btn-sm">
                    Open →
                  </button>
                  <button onClick={() => setSelectedPkg(null)} className="btn btn-ghost btn-sm">✕</button>
                </div>
              </div>
              <div className="card-body">
                <div className="grid-3">
                  {[
                    ['Items', selectedPkg.item_count || 0],
                    ['RFQs', selectedPkg.rfq_count || 0],
                    ['Quotes', selectedPkg.quote_count || 0],
                  ].map(([label, value]) => (
                    <div key={label} style={{ padding: '10px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{value}</div>
                    </div>
                  ))}
                </div>
                {selectedPkg.scope_summary && (
                  <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--color-teal-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-teal)', fontSize: '0.8125rem', color: 'var(--color-text-dark)', lineHeight: 1.6 }}>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-teal)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Scope</div>
                    {selectedPkg.scope_summary}
                  </div>
                )}
                {selectedPkg.rfq_due_date && (
                  <div style={{ marginTop: 10, fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>
                    RFQ Due: <strong>{new Date(selectedPkg.rfq_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Intelligence Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Action list */}
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Action Required</span>
              {actions.length > 0 && (
                <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-error-bg)', color: 'var(--color-error)' }}>
                  {actions.length}
                </span>
              )}
            </div>
            {actions.length === 0 ? (
              <div style={{ padding: '20px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.25rem', marginBottom: 8 }}>✓</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-success)', fontWeight: 500 }}>All clear</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginTop: 4 }}>No actions required right now</div>
              </div>
            ) : (
              <div>
                {actions.map((action, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      if (action.type === 'rfq') navigate('/rfq');
                      else if (action.type === 'review') navigate('/bid-leveling');
                      else if (action.type === 'award') navigate('/bid-leveling');
                      else if (action.type === 'extraction') navigate('/extract');
                      else if (action.pkgs?.[0]) navigate(`/packages/${action.pkgs[0].id}`);
                    }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '12px 16px',
                      borderBottom: i < actions.length - 1 ? '1px solid var(--color-border)' : 'none',
                      cursor: 'pointer', transition: 'background 0.1s',
                      borderLeft: `3px solid ${action.color}`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '1rem', color: action.color, flexShrink: 0, marginTop: 1 }}>{action.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-dark)', lineHeight: 1.4 }}>
                        {action.label}
                      </div>
                      {action.pkgs?.length > 0 && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {action.pkgs.map(p => p.name).join(', ')}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: action.color, flexShrink: 0 }}>→</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Procurement summary */}
          <div className="card">
            <div className="card-header"><span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Lifecycle Summary</span></div>
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {LIFECYCLE_COLUMNS.map(col => {
                const count = packages.filter(p => col.states.includes(p.lifecycle_state || 'draft_scope')).length;
                return (
                  <div key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-mid)', width: 80, flexShrink: 0 }}>{col.label}</div>
                    <div style={{ flex: 1, height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 2,
                        width: packages.length > 0 ? `${(count / packages.length) * 100}%` : '0%',
                        background: count > 0 ? 'var(--color-teal)' : 'transparent',
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                    <div style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--color-text-light)', width: 16, textAlign: 'right' }}>{count}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick links */}
          <div className="card">
            <div className="card-header"><span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Quick Actions</span></div>
            <div style={{ padding: '8px 0' }}>
              {[
                { label: 'Upload Documents', path: '/documents', icon: '↑' },
                { label: 'Review Items', path: '/extract', icon: '◈' },
                { label: 'Manage Packages', path: '/packages', icon: '▦' },
                { label: 'Track RFQs', path: '/rfq', icon: '✉' },
                { label: 'Bid Leveling', path: '/bid-leveling', icon: '⚖' },
                { label: 'Suppliers', path: '/suppliers', icon: '◉' },
              ].map(link => (
                <div
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', cursor: 'pointer', transition: 'background 0.1s', fontSize: '0.8125rem', color: 'var(--color-text-dark)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ color: 'var(--color-teal)', width: 16, textAlign: 'center' }}>{link.icon}</span>
                  {link.label}
                  <span style={{ marginLeft: 'auto', color: 'var(--color-text-light)', fontSize: '0.75rem' }}>→</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}